import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface SpoilerInfoTooltipProps {
  description: string;
  className?: string;
}

const SpoilerInfoTooltip: React.FC<SpoilerInfoTooltipProps> = ({ description, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0, arrowLeft: 0, below: false });
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const tooltipWidth = 224;
      const left = Math.max(8, Math.min(
        rect.left + rect.width / 2 - tooltipWidth / 2,
        window.innerWidth - tooltipWidth - 8
      ));
      const below = rect.top <= 110;
      const top = below ? rect.bottom + 8 : rect.top - 8;
      setPosition({
        left,
        top,
        arrowLeft: rect.left + rect.width / 2 - left - 4,
        below,
      });
    };

    const closeOnOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };

    updatePosition();
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('touchstart', closeOnOutsideClick);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('touchstart', closeOnOutsideClick);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="راهنمای لو نده"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((previous) => !previous)}
        className="flex h-3 w-3 items-center justify-center rounded-full bg-gray-400 p-0 text-center font-sans text-[8px] font-bold leading-[12px] text-white transition-colors hover:bg-gray-500"
      >
        ?
      </button>
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          role="tooltip"
          dir="rtl"
          className={`pointer-events-none fixed z-[9999] w-56 rounded-xl bg-gray-800 px-3 py-2 text-center font-persian text-[11px] leading-5 text-white shadow-lg ${position.below ? '' : '-translate-y-full'}`}
          style={{ left: position.left, top: position.top }}
        >
          {description}
          <span
            className={`absolute left-0 h-2 w-2 rotate-45 bg-gray-800 ${position.below ? '-top-1' : '-bottom-1'}`}
            style={{ left: position.arrowLeft }}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default SpoilerInfoTooltip;
